import React from "react";
import { BCMSRecord, useRoute, useBCMSContext, DisabledPrompt, Branding, useStateManager } from "bcms-react";
import Classes, { ContactInfo, NotificationDetail, Notification, ScheduledAlert, ScheduledTime } from "model"; 

import UINotifiToggle from "components/shared/notifi/UINotifiToggle";
import { SVGAsset } from "res";

import "./mobile.css";
import "./styles.css";

export default function NotificationAlertSectionContainer(props) {
    const category = props.category ?? {};
    const alerts = props.alerts ?? [];

    const text = category.name ?? "[Notifications]";
    const name = category.description ?? "[CategoryName]";

    const alertItems = alerts.map(alert => <NotificationAlert key={ alert.id } alert={ alert } />, []);

    return (
        <div className="notification-alert-section-container">
            <p className="alert-category-title">{text}</p>
            <p className="alert-type-title sub-text">({name})</p>

            { alertItems }
        </div>
    );
}

function NotificationAlert(props) {
    const navigate = useRoute();
    const controller = useBCMSContext();
    const { updateNotifications, updateUI } = useStateManager();

    const alert  = props.alert ?? {};

    const description = alert.name ?? "[Description";
    const selected = alert.activated ?? false;

    let phoneSelected = selected;
    let emailSelected = selected;
    let alertSelected = selected;

    let notifications = NotificationDetail.getByNotificationID(alert.id);
    let hasNotification = notifications.length !== 0;
    
    if(hasNotification && selected) {
        phoneSelected = notifications.filter(notification => {
            const contactInfoPhone = notification.contactInfoEnabledIDs.filter(id => {
                return id === "" ? false : ContactInfo.initByID(id).type === "phone";
            });

            return notification.isEnabled && contactInfoPhone.length > 0;
        }).length > 0;

        emailSelected = notifications.filter(notification => {
            const contactInfoEmail = notification.contactInfoEnabledIDs.filter(id => {
                return id === "" ? false : ContactInfo.initByID(id).type === "email";
            });

            return notification.isEnabled && contactInfoEmail.length > 0;
        }).length > 0;

        alertSelected = notifications.filter(notification => {
            const contactInfoAlert = notification.contactInfoEnabledIDs.filter(id => {
                return id === "" ? false : ContactInfo.initByID(id).type === "alert";
            });

            return notification.isEnabled && contactInfoAlert.length > 0;
        }).length > 0;
    }

    const resetScheduledAlert = alert => {
        const _alert = ScheduledAlert.init({ ...alert.record });
        _alert.hour = _alert.minute = ""
        _alert.timeOfDay = "AM";
        _alert.isEnabled = false;
        _alert.weekdayIDs = [];
        return _alert;
    };

    const resetScheduledTime = alert => {
        const scheduledTime = ScheduledTime.init({ ...alert.scheduledTime.record });
        scheduledTime.hour = scheduledTime.minute = ""
        scheduledTime.timeOfDay = "AM";
        return scheduledTime;
    };

    const showAccountDetailsToggle = evt => {
        if(hasNotification) {
            if(!selected) { navigate(`/alertDetails/${ alert.id }`); }
            else {
                evt.stopPropagation();
                evt.preventDefault();

                // For this alert, update all of its notification details.
                const updates = notifications.reduce((acc, notification) => {
                    // For this notification detail, reset a scheduled alert if one exists.
                    if(notification.scheduledAlert) {
                        acc.push(
                            ScheduledAlert.update({
                                ...resetScheduledAlert(notification.scheduledAlert).record
                            })
                        );
                        acc.push(
                            ScheduledTime.update({
                                ...resetScheduledTime(notification.scheduledAlert).record
                            })
                        );
                    }

                    // Disable this notification detail.
                    acc.push(
                        NotificationDetail.update({ 
                            ...notification.record,
                            isEnabled: false
                        })
                    );

                    return acc;
                }, []);

                const updatedAlert = Notification.init({
                    ...alert.record,
                    subscriptionRecentlyDeleted: true
                });

                BCMSRecord.batch(updates, controller, Classes);
                updateNotifications(updatedAlert);
                updateUI();
            }
        }

        return true;
    }
    const showAccountDetails = evt => hasNotification ? navigate(`/alertDetails/${ alert.id }`) : undefined;

    return(   
        <>
            <div className="flex-container flex-horizontal align-center alert-item">
                <DisableRoutes disable={hasNotification === false}>
                    <UINotifiToggle className="set-width" value={selected} onClick={ showAccountDetailsToggle } dataPoint={`notification_toggle_${props.alert.id}`}/>
                </DisableRoutes> 

                <DisableRoutes disable={hasNotification === false}>
                    <p className="flex-width description clickable" onClick={showAccountDetails}>
                        <span data-point={`notification_description_${props.alert.id}`}>{description}</span>
                    </p>
                </DisableRoutes>
                
                <div className="active-icons flex-container flex-horizontal align-center set-width">
                    <Branding id="notifi_version_abi22" value = "Notifi">
                        <div className={`${alertSelected?"active":""} flex-container align-center-container`} data-point={ `notification_alert_phone_${ props.alert.id }` }>
                            <SVGAsset id="MobilePhoneIcon" />
                        </div>
                    </Branding>

                    <Branding id="notifi_version_abi22" value = "Notifi">
                        <div className={`${phoneSelected?"active":""} flex-container align-center-container`} data-point={ `notification_alert_message_${ props.alert.id }` }>
                            <SVGAsset id="SquareMessageBubbleIcon" className="dark-icon" />
                        </div>
                    </Branding> 

                    <div className={`${emailSelected?"active":""} flex-container align-center-container`} data-point={ `notification_alert_email_${ props.alert.id }` }>
                        <SVGAsset id="EnvelopeIcon" className="dark-icon" />
                    </div>
                </div>
                
                <div className="right-chevron align-center set-width flex-container justify-right clickable">
                    <DisableRoutes  disable={hasNotification === false}> <SVGAsset id="RightChevronIcon" onClick={showAccountDetails}/></DisableRoutes> 
                </div>
            </div> 
            <div>
                { alert.subscriptionRecentlyDeleted && <span className="successful">Subscription deleted successfully.</span> }
            </div>
        </>
    );
}

function DisableRoutes(props) {
    if(props.disable) { return <DisabledPrompt>{ props.children }</DisabledPrompt> }

    return props.children;
}